import { Chakra } from "@airhelp/react-ui-components";
import BreadcrumbsSchema from "modules/storyblok/components/BreadcrumbsSchema";
import { stickyBottomBarFirstTriggerStyles } from "components/StickyBottomBar";
import StickyBottomBarTrigger from "components/StickyBottomBar/StickyBottomBarTrigger";
import { DataContextProvider } from "contexts/DataContext";
import { useLanguage } from "contexts/LocalizationContext";
import { MobileMenuContextProvider } from "contexts/MobileMenuContext";
import { ModalContextProvider } from "contexts/ModalContext";
import { StickyBottomBarContextProvider } from "contexts/StickyBottomBarContext";
import CustomHead from "modules/common/components/CustomHead";
import Footer from "modules/common/components/Footer";
import Grid from "modules/common/components/Grid";
import HeaderNav from "modules/common/components/HeaderNav";
import CheckCompensationButton from "modules/common/components/HeaderNav/CheckCompensationButton";
import MenuItems from "modules/common/components/HeaderNav/MenuItems";
import PressLogos from "modules/common/components/PressLogos";
import { useTopMenu } from "utils/menuUtils";
import StickyBottomBarAdapter from "../../adapters/StickyBottomBar";
import CallToAction from "./CallToAction";
import CategoryList from "./CategoryList";
import ContactUs from "./ContactUs";
import Header from "./Header";
import type { HelpHomeTemplateProps } from "./HelpHomeTemplate.d";
import QuestionsAccordion from "./QuestionsAccordion";

const { Box } = Chakra;

const HelpHomeTemplate = ({
  story,
  cmsLanguagesData,
  serverSideStoryblokPages,
  mostCommonQuestionsCategory,
  categories,
  whiteListDomains,
}: HelpHomeTemplateProps) => {
  const language = useLanguage();
  const topMenu = useTopMenu(language);

  const {
    tag_list: tagList,
    content: {
      metaTitle,
      metaDescription,
      breadcrumbsParentPage,
      breadcrumbsPositionName,
      stickyBottomBarHeading,
      hideStickyBottomBar,
      stickyBottomBarVariant,
    },
  } = story;
  const noindex = tagList.includes("noindex");

  return (
    <DataContextProvider data={{ cmsLanguagesData, serverSideStoryblokPages, whiteListDomains }}>
      <CustomHead title={metaTitle} description={metaDescription} noindex={noindex} />
      <BreadcrumbsSchema
        breadcrumbsPositionName={breadcrumbsPositionName}
        breadcrumbsParentPage={breadcrumbsParentPage}
      />
      <MobileMenuContextProvider>
        <ModalContextProvider>
          <HeaderNav menuItems={<MenuItems menu={topMenu} />} headerButtons={<CheckCompensationButton />} />
          <Box as="main">
            <StickyBottomBarTrigger type="first" styles={stickyBottomBarFirstTriggerStyles} />
            <Header />
            <QuestionsAccordion category={mostCommonQuestionsCategory} />
            <CallToAction />
            <CategoryList categories={categories} />
            <Grid>
              <ContactUs />
            </Grid>
            <PressLogos />
          </Box>
          <Footer />
        </ModalContextProvider>
      </MobileMenuContextProvider>
      <StickyBottomBarAdapter
        showStickyBottomBar={!hideStickyBottomBar}
        stickyBottomBarVariant={stickyBottomBarVariant}
        stickyBottomBarHeading={stickyBottomBarHeading}
      />
    </DataContextProvider>
  );
};

const HelpHomeTemplateAdapter = (props) => (
  <StickyBottomBarContextProvider threshold={0.4}>
    <HelpHomeTemplate {...props} />
  </StickyBottomBarContextProvider>
);

export default HelpHomeTemplateAdapter;
